import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import { SentimentVerySatisfied, Close, Launch } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PaperSection from '../../components/PaperSection';
import ContentWrapper from '../../layout/ContentWrapper';
import { useNotifications } from '../../contexts/NotificationsContext';
import { Notification, UserRole } from '@avanzu/types';
import getSeverityColor from '../../helpers/getSeverityColor';
import { EmptyStateContent } from '../../components/EmptyStateContent';

export const NotificationRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

export const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, dismissNotification } = useNotifications();

  const handleActionClick = async (notification: Notification) => {
    if (notification.actionUrl) {
      await dismissNotification(notification.id);
      navigate(notification.actionUrl);
    }
  };

  const handleDismiss = async (notificationId: string) => {
    await dismissNotification(notificationId);
  };

  return (
    <ContentWrapper breadcrumb>
      <PaperSection title="Notifications">
        {notifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <EmptyStateContent
              icon={<SentimentVerySatisfied sx={{ fontSize: 64, mb: 2 }} />}
              title="All caught up!"
              message="You have no notifications at the moment."
              showBorder={false}
            />
          </Box>
        ) : (
          <Stack spacing={2}>
            {notifications.map((notification) => (
              <Card key={notification.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={notification.severity.toUpperCase()}
                      color={getSeverityColor(notification.severity)}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDismiss(notification.id)}
                      sx={{ ml: 1 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(notification.createdAt).toLocaleString()}
                  </Typography>
                </CardContent>
                {notification.actionUrl && notification.actionText && (
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<Launch />}
                      onClick={() => handleActionClick(notification)}
                    >
                      {notification.actionText}
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Notifications;
